import { amplitudeLib } from 'freedom-shared';

/**
 * Discriminates the booking funnel between the rolled-out consolidated
 * QuickBookingPage (`quick_booking`) and the legacy per-step flow
 * (`classic`), which still serves ineligible offers (FunSun/charter).
 * Shared events (booking success, products page, payment, price-change
 * guard) carry this so a single Amplitude funnel can split by flow.
 */
export type QuickBookingFlow = 'quick_booking' | 'classic';

export type QuickBookingFallbackReason =
  | 'offer_expired'
  | 'double_booking'
  | 'init_error_5xx'
  | 'init_error_4xx'
  | 'validation_failed'
  | 'funsun_leaked'
  | 'submit_failed';

interface PaxBreakdown {
  adt: number;
  chd: number;
  inf: number;
  yth: number;
}

interface PageOpenParams {
  offerId: string;
  searchQuery: string;
  providerClass: string;
  hasWarnings: boolean;
  tripType: 'OW' | 'RT';
  paxCount: number;
  paxBreakdown: PaxBreakdown;
  isDomestic: boolean;
  preselectedPackageId: string | null;
}

interface PassengerSavedParams {
  offerId: string;
  providerClass: string;
  isDomestic: boolean;
}

interface PayClickParams {
  offerId: string;
  providerClass: string;
  isDomestic: boolean;
  totalPrice: string;
  ancillaryBaggageCount: number;
  hasSeats: boolean;
  hasInsurance: boolean;
}

interface FallbackParams {
  offerId: string;
  providerClass: string;
  isDomestic: boolean;
  reason: QuickBookingFallbackReason;
  httpStatus?: number;
  errorMessage?: string;
}

/**
 * The booking events below intentionally carry rich, per-event context
 * (offer id, provider class, passenger breakdown, price, extras flags).
 * Support relies on these payloads in Amplitude to resolve customer cases,
 * so keep them enriched rather than trimming to bare identifiers.
 */
export const bookingPageOpen = (params: PageOpenParams) =>
  amplitudeLib.logEvent('avfree_booking_page_open', {
    offer_id: params.offerId,
    search_query: params.searchQuery,
    provider_class: params.providerClass,
    has_warnings: params.hasWarnings,
    search_mode: params.tripType,
    passenger_count: params.paxCount,
    passenger_adt: params.paxBreakdown.adt,
    passenger_chd: params.paxBreakdown.chd,
    passenger_inf: params.paxBreakdown.inf,
    passenger_yth: params.paxBreakdown.yth,
    is_domestic: params.isDomestic,
    preselected_package_id: params.preselectedPackageId,
  });

export const bookingPassengerSaved = (params: PassengerSavedParams) =>
  amplitudeLib.logEvent('avfree_booking_passenger_saved', {
    offer_id: params.offerId,
    provider_class: params.providerClass,
    is_domestic: params.isDomestic,
  });

export const bookingPayClick = (params: PayClickParams) =>
  amplitudeLib.logEvent('avfree_booking_pay_click', {
    offer_id: params.offerId,
    provider_class: params.providerClass,
    is_domestic: params.isDomestic,
    total_price: params.totalPrice,
    ancillary_baggage_count: params.ancillaryBaggageCount,
    has_seats: params.hasSeats,
    has_insurance: params.hasInsurance,
  });

export const bookingFallback = (params: FallbackParams) =>
  amplitudeLib.logEvent('avfree_booking_fallback', {
    offer_id: params.offerId,
    provider_class: params.providerClass,
    is_domestic: params.isDomestic,
    reason: params.reason,
    http_status: params.httpStatus,
    error_message: params.errorMessage,
  });
